import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import MainLayout from "./MainLayout";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  Box,
  Button,
  TableSortLabel,
  Grid,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Typography,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  Search,
  ContentCopy,
  Visibility,
  InsertDriveFile,
  Image,
  VideoLibrary,
  Description,
  Article,
  PictureAsPdf,
  Download,
} from "@mui/icons-material";
import TableChartIcon from "@mui/icons-material/TableChart";
import { TableBodySkeleton } from "../utils/TableSkeleton";
import { fetchMailLog } from "../../redux/slices/userEngagementSlice";
import MultiSelectWithCheckbox from "../utils/MultiSelectWithCheckbox";
import { fetchUserOption } from "../../redux/slices/clientSlice";
import MultipleStopIcon from "@mui/icons-material/MultipleStop";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import RoleSelect from "../utils/RoleSelect";
import { ClickableTableLink } from "./ClickableTableLink";
import FilterBar2 from "./admin_dashboard/FilterBar2";
import { FY_YEAR_OPTIONS } from "../utils/FilterOptions";

const EMAIL_TYPE_OPTIONS = [
  { label: "Automatic", value: "A" },
  { label: "Manual", value: "M" },
];

const roleOpt = [
  { label: "IC", value: 2 },
  { label: "BSP", value: 3 },
  { label: "DSP", value: 4 },
  { label: "BD", value: 5 },
  { label: "Sales Enabler", value: 6 },
  { label: "Dealer", value: 7 },
  { label: "Studio", value: 8 },
];

const MailLog = () => {
  const dispatch = useDispatch();
  const { mailLog, count, status } = useSelector((state) => state.engagement);
  const counts = count || 0;
  const location = useLocation();
  const navState = location.state || {};

  const [selectedFromDate, setSelectedFromDate] = useState(null);
  const [selectedToDate, setSelectedToDate] = useState(null);
  const [roleId, setRoleId] = useState([]);
  const [selectedUser, setSelectedUser] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [emailTypeFilter, setEmailTypeFilter] = useState([]);
  
  const [month, setMonth] = useState(() => {
    if (navState && navState.months) return navState.months;
    const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
    const currentCalendarMonth = new Date().getMonth() + 1;
    const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
    return fullFYMonths.slice(0, currentMonthIndex + 1);
  });
  
  const [year, setYear] = useState(() => {
    if (navState.years?.length > 0) {
      const targetYrs = navState.years.map(String);
      return FY_YEAR_OPTIONS.filter((y) => targetYrs.includes(y.value));
    }
    const currentYrStr = String(new Date().getFullYear());
    return FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr);
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [sortBy, setSortBy] = useState({
    sort_by: "date",
    order: "desc",
  });
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const { userOption } = useSelector((state) => state.client);

  const isUsingDateRange = Boolean(selectedFromDate || selectedToDate);
  const hasValidFilters = isUsingDateRange || Boolean(year?.length > 0 && month?.length > 0);

  useEffect(() => {
    if (!hasValidFilters) {
      return;
    }
    const selectedType = emailTypeFilter?.length > 0 ? emailTypeFilter[0].value : "all";

    dispatch(
      fetchMailLog({
        search: searchQuery,
        page,
        rowsPerPage,
        sort_by: sortBy.sort_by,
        sort_order: sortBy.order,
        role: roleId?.map((r) => r.value),
        from: selectedFromDate,
        to: selectedToDate,
        users: selectedUser?.map((u) => u.value),
        type: selectedType === "all" ? null : selectedType,
        month: month?.join(',') || "",
        year: year?.map((y) => y.value)?.join(',') || "",
      })
    );
  }, [
    searchQuery,
    page,
    rowsPerPage,
    sortBy,
    roleId,
    selectedFromDate,
    selectedToDate,
    selectedUser,
    emailTypeFilter,
    month,
    year,
    hasValidFilters,
    dispatch,
  ]);

  useEffect(() => {
    dispatch(fetchUserOption());
  }, [dispatch]);

  const userOpt = useMemo(() => {
    return (
      userOption?.map((user) => ({
        value: user.id,
        label: `${user.name} ${user.last_name} (${user.emp_code})`,
      })) || []
    );
  }, [userOption]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEmailDialog = (email) => {
    setSelectedEmail(email);
    setEmailDialogOpen(true);
  };

  const handleViewEmail = (email) => {
    handleOpenEmailDialog(email);
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setSelectedEmail(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB");
  };

  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const handleSort = (column) => {
    setSortBy((prev) => {
      if (prev.sort_by === column) {
        return {
          sort_by: column,
          order: prev.order === "asc" ? "desc" : "asc",
        };
      }
      return { sort_by: column, order: "asc" };
    });
  };

  const handleDownloadExcel = () => {
    if (!mailLog || mailLog.length === 0) return;

    const excelData = mailLog.map((item) => ({
      "EMP Code": item.emp_code || "N/A",
      "EMP Name": item.emp_name,
      Email: item.email,
      "Sent Date": item.sent_date,
      "Sent Time": item.sent_time,
      "Total Clients": item.total_client,
      "Total Quotes": item.total_quotes,
      "Email Type": item.email_type,
      Subject: item.subject,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    const wscols = [
      { wch: 12 }, // EMP Code
      { wch: 20 }, // EMP Name
      { wch: 25 }, // Email
      { wch: 12 }, // Sent Date
      { wch: 10 }, // Sent Time
      { wch: 12 }, // Total Clients
      { wch: 12 }, // Total Quotes
      { wch: 10 }, // Email Type
      { wch: 40 }, // Subject
    ];
    worksheet["!cols"] = wscols;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Mail Log");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(data, `Mail_Log_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const getEmailTypeColor = (type) => {
    switch (type?.toUpperCase()) {
      case "A":
        return "primary"; // Automatic
      case "M":
        return "secondary"; // Manual
      default:
        return "default";
    }
  };

  const getEmailTypeLabel = (type) => {
    switch (type?.toUpperCase()) {
      case "A":
        return "Automatic";
      case "M":
        return "Manual";
      default:
        return "Unknown";
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      Swal.fire({
        icon: "success",
        title: "Copied!",
        text: "Text copied to clipboard successfully.",
        timer: 3000,
        showConfirmButton: false,
        toast: true,
        position: "bottom-end",
        timerProgressBar: true,
      });
    });
  };

    const filterConfigs = [
    { id: "user", label: "User", options: userOpt || [], selected: selectedUser },
    { id: "role", label: "Role", options: roleOpt || [], selected: roleId },
    { id: "emailType", label: "Email Type", options: EMAIL_TYPE_OPTIONS, selected: emailTypeFilter, isSingle: true },
    { id: "year", label: "Year (FY)", options: FY_YEAR_OPTIONS, selected: year },
  ];

  const handleMonthChange = (newMonths) => {
    setMonth(newMonths);
    if (newMonths?.length > 0 && year?.length === 0) {
      const currentYrStr = String(new Date().getFullYear());
      setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
    }
    setPage(0);
  };

  const handleFilterChange = (filterId, nextSelected) => {
    switch (filterId) {
      case "user": setSelectedUser(nextSelected); break;
      case "role": setRoleId(nextSelected); break;
      case "emailType": {
        const single = Array.isArray(nextSelected) && nextSelected.length > 0
          ? [nextSelected[nextSelected.length - 1]]
          : [];
        setEmailTypeFilter(single);
        break;
      }
      case "year": 
        setYear(nextSelected); 
        if (nextSelected.length === 0) {
          setMonth([]);
        } else if (month?.length === 0) {
          const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
          const currentCalendarMonth = new Date().getMonth() + 1;
          const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
          setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));
        }
        break;
      default: break;
    }
    setPage(0);
  };

  const handleClearFilters = () => {
    setSelectedUser([]);
    setRoleId([]);
    setEmailTypeFilter([EMAIL_TYPE_OPTIONS[0]]);
    setSelectedFromDate(null);
    setSelectedToDate(null);
    setSearchQuery("");
    setSortBy({ sort_by: "date", order: "desc" });
    setPage(0);

    const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
    const currentCalendarMonth = new Date().getMonth() + 1;
    const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
    setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));

    const currentYrStr = String(new Date().getFullYear());
    setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
  };

  return (
    <MainLayout title={'Mail Logs'}>
      <Box sx={{ p: 0 }}>
        <FilterBar2
          filters={filterConfigs}
          onFilterChange={handleFilterChange}
          selectedMonth={month}
          setSelectedMonth={handleMonthChange}
          searchTerm={searchQuery}
          setSearchTerm={(val) => { setSearchQuery(val); setPage(0); }}
          selectedFromDate={selectedFromDate}
          setSelectedFromDate={(val) => { setSelectedFromDate(val); setPage(0); }}
          selectedToDate={selectedToDate}
          setSelectedToDate={(val) => { setSelectedToDate(val); setPage(0); }}
          onClearFilters={handleClearFilters}
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="contained"
                startIcon={<TableChartIcon />}
                onClick={handleDownloadExcel}
                sx={{
                  textTransform: "none",
                  bgcolor: "#2e7d32",
                  py: "2px",
                  px: "14px",
                  borderRadius: 12,
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: "#1b5e20" },
                }}
              >
                Excel
              </Button>
            </div>
          }
        />
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "0px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Table stickyHeader>
            <TableHead sx={{ background: "#04121f", color: "white" }}>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    color: "white",
                    whiteSpace: "nowrap",
                    background: "#04121f",
                  }}
                >
                  Sr. No.
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    color: "white",
                    whiteSpace: "nowrap",
                    background: "#04121f",
                  }}
                >
                  <TableSortLabel
                    active={sortBy.sort_by === "emp_name"}
                    direction={
                      sortBy.sort_by === "emp_name" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("emp_name")}
                    hideSortIcon={false}
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1,
                        color: "white !important",
                      },
                    }}
                  >
                    EMP Name
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    color: "white",
                    whiteSpace: "nowrap",
                    background: "#04121f",
                  }}
                >
                  <TableSortLabel
                    active={sortBy.sort_by === "email"}
                    direction={
                      sortBy.sort_by === "email" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("email")}
                    hideSortIcon={false}
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1,
                        color: "white !important",
                      },
                    }}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    color: "white",
                    whiteSpace: "nowrap",
                    background: "#04121f",
                  }}
                >
                  <TableSortLabel
                    active={sortBy.sort_by === "sent_date"}
                    direction={
                      sortBy.sort_by === "sent_date" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("sent_date")}
                    hideSortIcon={false}
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1,
                        color: "white !important",
                      },
                    }}
                  >
                    Sent Date
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    color: "white",
                    whiteSpace: "nowrap",
                    background: "#04121f",
                  }}
                >
                  <TableSortLabel
                    active={sortBy.sort_by === "sent_time"}
                    direction={
                      sortBy.sort_by === "sent_time" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("sent_time")}
                    hideSortIcon={false}
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1,
                        color: "white !important",
                      },
                    }}
                  >
                    Sent Time
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    color: "white",
                    whiteSpace: "nowrap",
                    background: "#04121f",
                  }}
                >
                  Total Clients
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    color: "white",
                    whiteSpace: "nowrap",
                    background: "#04121f",
                  }}
                >
                  Total Quotes
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    color: "white",
                    whiteSpace: "nowrap",
                    background: "#04121f",
                  }}
                >
                  Email Type
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    color: "white",
                    whiteSpace: "nowrap",
                    background: "#04121f",
                  }}
                >
                  Subject
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    color: "white",
                    whiteSpace: "nowrap",
                    background: "#04121f",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                if (!hasValidFilters) {
                  return (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        No data
                      </TableCell>
                    </TableRow>
                  );
                }
                return mailLog?.length > 0 ? (
                  mailLog.map((item, index) => (
                    <TableRow
                      hover
                      key={item.id}
                      sx={{
                        cursor: "pointer",
                        "&.Mui-selected": {
                          backgroundColor: "rgba(2, 136, 209, 0.1)",
                        },
                      }}
                    >
                      <TableCell align="center">
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" fontWeight="medium">
                            <ClickableTableLink path="/admin/userlist" displayValue={item.emp_name} searchValue={item.emp_code} />
                          </Typography>
                          {item.emp_code && (
                            <Typography variant="caption" color="text.secondary">
                              {item.emp_code}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={item.email}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "150px",
                            }}
                          >
                            {item.email}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={item.role_name || "N/A"}
                          size="small"
                          sx={{
                            bgcolor: "#e0f2fe",
                            color: "#0288d1",
                            fontWeight: "medium",
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {formatDate(item.created_at)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(item.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="medium">
                          {item.total_dealers || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="medium">
                          {item.total_quotes || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getEmailTypeLabel(item.email_type)}
                          color={getEmailTypeColor(item.email_type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={item.subject}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "200px",
                            }}
                          >
                            {item.subject}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Email Content">
                          <IconButton
                            size="small"
                            onClick={() => handleViewEmail(item)}
                            sx={{
                              color: "#0288d1",
                              "&:hover": {
                                backgroundColor: "rgba(2, 136, 209, 0.1)",
                              },
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : status === "pending" ? (
                  <TableBodySkeleton cols={10} rows={10} />
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No data found
                    </TableCell>
                  </TableRow>
                );
              })()}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={hasValidFilters ? counts : 0}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={hasValidFilters ? [10, 20, 50, 100, counts || 0] : [10, 20, 50, 100]}
          sx={{ borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px" }}
        />

        {/* Email Content Dialog */}
        <Dialog
          open={emailDialogOpen}
          onClose={handleCloseEmailDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Email Details
              </Typography>
            </Box>
            <IconButton onClick={handleCloseEmailDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {selectedEmail && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Header Info */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Employee
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedEmail.emp_name}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email Type
                    </Typography>
                    <Chip
                      label={getEmailTypeLabel(selectedEmail.email_type)}
                      color={getEmailTypeColor(selectedEmail.email_type)}
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Sent Date & Time
                    </Typography>
                    <Typography variant="body1">
                      {new Date(selectedEmail.sent_date).toLocaleDateString(
                        "en-GB"
                      )}{" "}
                      at {selectedEmail.sent_time}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email Address
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body1">
                        {selectedEmail.email}
                      </Typography>
                      <Tooltip title="Copy email">
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(selectedEmail.email)}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>

                {/* Statistics */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 3,
                    p: 2,
                    bgcolor: "#f9f9f9",
                    borderRadius: 1,
                    mb: 2,
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Clients
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {selectedEmail.total_client}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Quotes
                    </Typography>
                    <Typography variant="h6" color="secondary">
                      {selectedEmail.total_quotes}
                    </Typography>
                  </Box>
                </Box>

                {/* Subject */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Subject
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    sx={{
                      p: 1.5,
                      bgcolor: "#f0f7ff",
                      borderRadius: 1,
                      borderLeft: "4px solid #0288d1",
                    }}
                  >
                    {selectedEmail.subject}
                  </Typography>
                </Box>

                {/* Message Content */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Message Content
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      minHeight: "200px",
                      maxHeight: "400px",
                      overflowY: "auto",
                      bgcolor: "#fafafa",
                      borderRadius: 1,
                      fontFamily: "inherit",
                      lineHeight: 1.6,
                    }}
                  >
                    {selectedEmail.email_type?.toUpperCase() === "M" ? (
                      <div
                        className="html-content"
                        dangerouslySetInnerHTML={{
                          __html: selectedEmail.message,
                        }}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {selectedEmail.message}
                      </Typography>
                    )}
                  </Paper>
                </Box>

                {/* Attachments */}
                {selectedEmail.attachments &&
                  selectedEmail.attachments.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Attachments ({selectedEmail.attachments.length})
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {selectedEmail.attachments.map((url, index) => {
                          const fileName = url.split("/").pop();
                          const extension = fileName
                            .split(".")
                            .pop()
                            .toLowerCase();

                          // Determine Icon based on extension
                          let FileIcon = <InsertDriveFile fontSize="small" />;
                          if (["jpg", "jpeg", "png", "gif"].includes(extension))
                            FileIcon = <Image fontSize="small" />;
                          if (["mp4", "webm", "ogg"].includes(extension))
                            FileIcon = <VideoLibrary fontSize="small" />;
                          if (["xls", "xlsx", "csv"].includes(extension))
                            FileIcon = (
                              <Description fontSize="small" color="success" />
                            );
                          if (["doc", "docx"].includes(extension))
                            FileIcon = (
                              <Article fontSize="small" color="primary" />
                            );
                          if (extension === "pdf")
                            FileIcon = (
                              <PictureAsPdf
                                fontSize="small"
                                sx={{ color: "#f44336" }}
                              />
                            );

                          return (
                            <Chip
                              key={index}
                              icon={FileIcon}
                              label={
                                fileName.length > 20
                                  ? `...${fileName.slice(-15)}`
                                  : fileName
                              }
                              component="a"
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              clickable
                              variant="outlined"
                              sx={{
                                maxWidth: "250px",
                                "&:hover": {
                                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                                },
                                cursor: "pointer",
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, bgcolor: "#f5f5f5" }}>
            <Button onClick={handleCloseEmailDialog} color="primary">
              Close
            </Button>
            {selectedEmail && (
              <Button
                variant="contained"
                onClick={() => {
                  copyToClipboard(selectedEmail.message);
                }}
                startIcon={<ContentCopy />}
              >
                Copy Content
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default MailLog;
